import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { useEffect } from 'react'
import { useI18n } from '../i18n'

export function Card({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={`rounded-3xl bg-card border border-line/70 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${className}`}
    >
      {children}
    </div>
  )
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-sm font-medium text-muted px-1 mb-2 tracking-wide">{children}</h2>
  )
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'soft' | 'danger'
}

export function Button({ variant = 'primary', className = '', ...props }: ButtonProps) {
  // 44px 起步，符合 iOS 的最小可点区域
  const base =
    'min-h-[44px] px-5 rounded-2xl font-medium transition active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100'
  const styles = {
    primary: 'bg-accent text-on-accent',
    soft: 'bg-bg-soft text-ink',
    ghost: 'text-muted',
    danger: 'bg-period-soft text-period',
  }[variant]
  return <button className={`${base} ${styles} ${className}`} {...props} />
}

export function Chip({
  active,
  onClick,
  children,
  tone = 'default',
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
  tone?: 'default' | 'period' | 'fertile'
}) {
  const activeStyles = {
    default: 'bg-ink text-bg border-ink',
    period: 'bg-period text-white border-period',
    fertile: 'bg-fertile text-white border-fertile',
  }[tone]
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-[40px] px-4 rounded-full border text-sm transition active:scale-95 ${
        active ? activeStyles : 'bg-bg-soft border-transparent text-ink/80'
      }`}
    >
      {children}
    </button>
  )
}

/** 从底部滑出的面板。点背景或按关闭都能收起。 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  const { t } = useI18n()

  // 面板打开时锁住背景滚动，不然 iOS 上会「穿透」到下面的页面
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div
        className="absolute inset-0 bg-black/35 animate-fade"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative bg-card rounded-t-[28px] max-h-[88vh] flex flex-col animate-sheet"
      >
        <div className="shrink-0 px-5 pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-line mx-auto mb-3" />
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">{title}</h2>
            <button
              onClick={onClose}
              className="w-9 h-9 -mr-1 rounded-full bg-bg-soft text-muted grid place-items-center"
              aria-label={t.common.close}
            >
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path
                  d="M2 2l11 11M13 2L2 13"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="overflow-y-auto px-5 pb-2 flex-1">{children}</div>
        {footer && <div className="shrink-0 px-5 pt-3 pb-5 safe-bottom border-t border-line/60">{footer}</div>}
      </div>
    </div>
  )
}

export function Spinner({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function Row({
  label,
  hint,
  children,
}: {
  label: ReactNode
  hint?: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="py-3 flex items-start justify-between gap-4 border-b border-line/60 last:border-0">
      <div className="min-w-0">
        <div className="text-[15px]">{label}</div>
        {hint && <div className="text-xs text-muted mt-1 leading-relaxed">{hint}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  )
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`w-[51px] h-[31px] rounded-full transition-colors relative ${
        checked ? 'bg-fertile' : 'bg-line'
      }`}
    >
      <span
        className="absolute top-[2px] left-[2px] w-[27px] h-[27px] rounded-full bg-white shadow transition-transform"
        style={{ transform: checked ? 'translateX(20px)' : 'none' }}
      />
    </button>
  )
}
